import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, FolderOpen, FileText, Clock, Users, Plus, File, Layers3, Archive } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import InlineCollectionCreator from '../InlineCollectionCreator';
import ContextMenu from '../ContextMenu';
import InlineRenameInput from '../InlineRenameInput';
import FileProcessingService from '../../services/FileProcessingService';
import exportService from '../../services/exportService';
import { useWebSocket } from '../../contexts/WebSocketContext';

export function CollectionsView({ project, userInfo, onBack, onCollectionClick, onFileSelect, searchQuery, sortBy, viewMode, onNewCollection, collectionsCache, onCollectionUpdated }) {
  const { joinProject, leaveProject, subscribe } = useWebSocket();
  const [collections, setCollections] = useState([]);
  const [allCollections, setAllCollections] = useState([]); // Store all collections for global search
  const [rootFiles, setRootFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // Track initial load separately
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [isSearching, setIsSearching] = useState(false); // Track search state
  const scrollContainerRef = useRef(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    collection: null,
    file: null,  // Add file support for root files
    type: null   // 'collection' or 'file'
  });
  
  // Rename state
  const [renamingCollection, setRenamingCollection] = useState(null);

  // File upload state - using hidden file input for project root uploads (like FilesView)
  const fileInputRef = useRef(null);

  // Download state
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  

  useEffect(() => {
    if (project && userInfo?.userId) {
      fetchCollections(1, '', true);

      // Join WebSocket room for this project
      joinProject(project._id);
      console.log(`👋 Joined project room: ${project._id}`);

      // Clean up: leave room when component unmounts or project changes
      return () => {
        leaveProject(project._id);
        console.log(`👋 Left project room: ${project._id}`);
      };
    }
  }, [project, userInfo]);

  // Define fetchCollections with useCallback to prevent infinite loops
  const fetchCollections = useCallback(async (page = 1, search = '', resetData = true) => {
    if (!project?._id || !userInfo?.userId) return;

    try {
      // Only show loading spinner if we have absolutely no data
      const hasExistingData = collections.length > 0 || rootFiles.length > 0;
      if (!hasExistingData && resetData) {
        setLoading(true);
      } else if (!resetData && page > 1) {
        setIsLoadingMore(true);
      }
      setError(null);
      console.log('🔍 Fetching paginated collections for:', { projectId: project._id, userId: userInfo.userId, page, search });

      // For global search, fetch larger chunks or use different strategy
      const pageSize = search ? 100 : 50; // Larger pages for search

      const paginatedData = await ProjectService.getProjectCollectionsPaginated(
        project._id,
        userInfo.userId,
        page,
        pageSize,
        search
      );

      console.log('📊 Paginated collections received:', {
        collectionsCount: paginatedData.collections?.length || 0,
        rootFilesCount: paginatedData.rootFilesCount || 0,
        totalItems: paginatedData.pagination?.totalItems || 0,
        currentPage: paginatedData.pagination?.currentPage || 1,
        totalPages: paginatedData.pagination?.totalPages || 1,
        collections: paginatedData.collections?.map(c => ({
          id: c._id,
          name: c.name,
          fileCount: c.fileCount
        })) || []
      });

      // Handle collections data
      if (resetData) {
        setCollections(paginatedData.collections || []);
        setAllCollections(paginatedData.collections || []);
      } else {
        setCollections(prev => [...prev, ...(paginatedData.collections || [])]);
        setAllCollections(prev => [...prev, ...(paginatedData.collections || [])]);
      }

      // Handle root files - fetch actual root files instead of placeholder
      if (page === 1) {
        if (paginatedData.rootFiles && paginatedData.rootFiles.length > 0) {
          setRootFiles(paginatedData.rootFiles);
        } else if (paginatedData.rootFilesCount > 0) {
          // If count but no files, fetch them separately
          fetchRootFiles();
        } else {
          setRootFiles([]);
        }
      }

      // Store pagination info
      setPagination(paginatedData.pagination);
      setCurrentPage(page);

    } catch (err) {
      console.error('Error fetching paginated collections:', err);
      setError(err.message || 'Failed to load collections');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      setInitialLoading(false);
    }
  }, [project, userInfo, collections.length, rootFiles.length]);

  // Handle search query changes - no longer triggers fetch
  useEffect(() => {
    // Just update search state, filtering happens in useMemo
    setIsSearching(!!searchQuery);
  }, [searchQuery]);

  // Subscribe to WebSocket events
  useEffect(() => {
    if (!project?._id) return;

    // Collection created event
    const unsubscribeCreated = subscribe('collection:created', (data) => {
      if (data.projectId === project._id) {
        console.log('📥 Received collection:created event:', data);
        // Check if collection already exists (avoid duplicates from own creation)
        setCollections(prev => {
          const exists = prev.some(c => c._id === data.collection._id);
          if (exists) {
            console.log('Collection already exists locally, skipping duplicate');
            return prev;
          }
          // Add new collection at the beginning for better UX
          return [data.collection, ...prev];
        });
        setAllCollections(prev => {
          const exists = prev.some(c => c._id === data.collection._id);
          if (exists) return prev;
          // Add new collection at the beginning for better UX
          return [data.collection, ...prev];
        });
      }
    });

    // Collection updated event
    const unsubscribeUpdated = subscribe('collection:updated', (data) => {
      if (data.projectId === project._id) {
        console.log('📥 Received collection:updated event:', data);
        setCollections(prev => prev.map(c =>
          c._id === data.collection._id ? data.collection : c
        ));
        setAllCollections(prev => prev.map(c =>
          c._id === data.collection._id ? data.collection : c
        ));
      }
    });

    // Collection deleted event
    const unsubscribeDeleted = subscribe('collection:deleted', (data) => {
      if (data.projectId === project._id) {
        console.log('📥 Received collection:deleted event:', data);
        setCollections(prev => prev.filter(c => c._id !== data.collectionId));
        setAllCollections(prev => prev.filter(c => c._id !== data.collectionId));
      }
    });

    // File uploaded event
    const unsubscribeFileUploaded = subscribe('file:uploaded', (data) => {
      if (data.projectId === project._id && !data.collectionId) {
        console.log('📥 Received file:uploaded event for root:', data);
        console.log('📥 File structure being added:', data.file);
        setRootFiles(prev => [...prev, data.file]);
      }
    });

    // File processing event for root files
    const unsubscribeFileProcessing = subscribe('file:processing', (data) => {
      if (data.projectId === project._id && !data.collectionId) {
        console.log('📥 Received file:processing event for root:', data);
        setRootFiles(prev => {
          console.log('Current root files:', prev.map(f => ({ id: f.id, _id: f._id, name: f.originalName })));
          return prev.map(file =>
            (file._id === data.fileId || file.id === data.fileId)
              ? { ...file, processingStatus: data.status }
              : file
          );
        });
      }
    });

    // File completed event for root files
    const unsubscribeFileCompleted = subscribe('file:completed', (data) => {
      if (data.projectId === project._id && !data.collectionId) {
        console.log('📥 Received file:completed event for root:', data);
        setRootFiles(prev => {
          console.log('Current root files:', prev.map(f => ({ id: f.id, _id: f._id, name: f.originalName })));
          return prev.map(file => {
            // Only update the file that matches the completed file ID
            const isMatchingFile = (file._id === data.file.id || file.id === data.file.id ||
                                   file._id === data.file._id || file.id === data.file._id);

            if (isMatchingFile) {
              return {
                ...file,
                processingStatus: 'completed',
                tableCount: data.file.tableCount,
                // Update file size if provided in the event
                fileSize: data.file.fileSize || file.fileSize,
                // Keep the original name, only update if it was missing
                originalName: file.originalName || file.name || data.file.name,
                // Keep the original type, only update if it was missing
                fileType: file.fileType || file.type || data.file.type
              };
            }
            // Return unchanged file if it doesn't match
            return file;
          });
        });
      }
    });

    // File failed event for root files
    const unsubscribeFileFailed = subscribe('file:failed', (data) => {
      if (data.projectId === project._id && !data.collectionId) {
        console.log('📥 Received file:failed event for root:', data);
        setRootFiles(prev => prev.map(file =>
          (file._id === data.fileId || file.id === data.fileId)
            ? { ...file, processingStatus: 'failed' }
            : file
        ));
      }
    });

    // File deleted event for root files
    const unsubscribeFileDeleted = subscribe('file:deleted', (data) => {
      if (data.projectId === project._id && !data.collectionId) {
        console.log('📥 Received file:deleted event for root:', data);
        setRootFiles(prev => prev.filter(file =>
          file._id !== data.fileId && file.id !== data.fileId
        ));
      }
    });

    // Cleanup
    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
      unsubscribeFileUploaded();
      unsubscribeFileProcessing();
      unsubscribeFileCompleted();
      unsubscribeFileFailed();
      unsubscribeFileDeleted();
    };
  }, [project, subscribe]);

  // Separate function to fetch root files
  const fetchRootFiles = async () => {
    try {
      const projectData = await ProjectService.getProjectHierarchy(project._id, userInfo.userId);
      if (projectData.rootFiles && projectData.rootFiles.length > 0) {
        setRootFiles(projectData.rootFiles);
      }
    } catch (err) {
      console.warn('Could not fetch root files:', err.message);
    }
  };

  const handleNewCollection = () => {
    setIsCreatingCollection(true);
    setCreateError(null);
    // Scroll to top to show the new collection creator
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleCreateSuccess = async (newCollection) => {
    console.log('✨ handleCreateSuccess called with:', newCollection);

    if (!newCollection) {
      console.error('newCollection is undefined or null');
      setCreateError('Failed to create collection: Invalid response from server');
      return;
    }

    // Don't update local state - WebSocket event will handle it
    // Just close the creator UI
    setIsCreatingCollection(false);
    setCreateError(null);

    // Update cache with new collection
    if (onCollectionUpdated) {
      onCollectionUpdated(newCollection._id, 'collectionCreated', { collection: newCollection });
    }

    // Notify parent if handler provided (legacy support)
    if (onNewCollection) {
      onNewCollection(newCollection);
    }

    console.log(`✅ Collection created, WebSocket will handle the update`);
  };

  const handleCreateError = (errorMessage) => {
    setCreateError(errorMessage);
  };

  const handleCreateCancel = () => {
    setIsCreatingCollection(false);
    setCreateError(null);
  };

  // Context menu handlers
  const handleRightClick = (e, collection) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      collection: collection,
      file: null,
      type: 'collection'
    });
  };

  const handleRightClickFile = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      collection: null,
      file: file,
      type: 'file'
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      collection: null,
      file: null,
      type: null
    });
  };

  const handleRename = () => {
    setRenamingCollection(contextMenu.collection);
    closeContextMenu();
  };

  const handleDeleteFile = async () => {
    const file = contextMenu.file;
    closeContextMenu();
    
    if (!file || !userInfo?.userId) return;
    
    const confirmMessage = `Are you sure you want to permanently delete "${file.originalName || file.name}"? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const result = await ProjectService.deleteFile(file._id, userInfo.userId);
      
      if (result.success) {
        // Remove the file from root files list
        setRootFiles(prev => prev.filter(f => f._id !== file._id));
        
        // Show success message
        console.log('File deleted successfully:', result.message);
        
        if (result.deletionSummary?.errors?.length > 0) {
          console.warn('Deletion completed with warnings:', result.deletionSummary.errors);
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    // Handle based on context menu type
    if (contextMenu.type === 'file') {
      return handleDeleteFile();
    }

    const collection = contextMenu.collection;
    closeContextMenu();

    if (window.confirm(`Are you sure you want to delete "${collection.name}"? This will permanently delete the collection and all its files. This action cannot be undone.`)) {
      try {
        console.log(`🗑️ Deleting collection ${collection._id}: "${collection.name}"`);

        await ProjectService.deleteCollection(collection._id, userInfo.userId);

        // Optimistic UI update
        setCollections(prev => prev.filter(c => c._id !== collection._id));

        // Update cache to remove deleted collection
        if (onCollectionUpdated) {
          onCollectionUpdated(collection._id, 'collectionDeleted');
        }

        console.log(`✅ Collection deleted successfully`);

      } catch (err) {
        console.error('❌ Error deleting collection with files:', err);
        setError(err.message || 'Failed to delete collection');
      }
    }
  };

  const handleCollapse = async () => {
    const collection = contextMenu.collection;
    closeContextMenu();

    console.log('🔄 Starting collapse operation for collection:', {
      collectionId: collection._id,
      collectionName: collection.name,
      fileCount: collection.fileCount
    });

    try {
      let movedFiles = [];

      // Move files to project root if there are any
      if (collection.fileCount > 0) {
        console.log('📁 Getting collection details to move files...');
        const collectionDetails = await ProjectService.getCollectionDetails(collection._id, userInfo.userId);
        console.log('📁 Collection details received:', {
          filesFound: collectionDetails.files?.length || 0,
          files: collectionDetails.files?.map(f => ({ id: f._id, name: f.originalName })) || []
        });

        const fileIds = collectionDetails.files?.map(file => file._id) || [];
        movedFiles = collectionDetails.files || [];

        if (fileIds.length > 0) {
          console.log('🚚 Moving files to project root:', fileIds);
          const moveResult = await ProjectService.moveFiles(collection._id, userInfo.userId, fileIds, null);
          console.log('🚚 Move files result:', moveResult);
        }
      }

      // Delete the empty collection
      console.log('🗑️ Deleting collection...');
      await ProjectService.deleteCollection(collection._id, userInfo.userId);
      console.log('🗑️ Collection deleted successfully');

      // Immediately update UI - remove collection from state
      setCollections(prev => prev.filter(c => c._id !== collection._id));

      // Add moved files to root files state immediately
      if (movedFiles.length > 0) {
        setRootFiles(prev => {
          // Avoid duplicates by filtering out files that might already exist
          const existingFileIds = new Set(prev.map(f => f._id));
          const newFiles = movedFiles.filter(f => !existingFileIds.has(f._id));
          return [...prev, ...newFiles];
        });
      }

      console.log('✅ Collapse operation completed successfully');
    } catch (err) {
      console.error('❌ Error collapsing collection:', err);
      setError(err.message || 'Failed to collapse collection');

      // On error, refresh to ensure UI is in sync with server state
      fetchCollections();
    }
  };

  // Rename handlers - with optimistic update and force refresh
  const handleRenameConfirm = async (newName) => {
    const collectionToRename = renamingCollection;

    try {
      console.log(`🏷️ Renaming collection ${collectionToRename._id}: "${collectionToRename.name}" → "${newName}"`);

      // Apply optimistic UI update immediately
      setCollections(prev =>
        prev.map(c =>
          c._id === collectionToRename._id ? { ...c, name: newName } : c
        )
      );

      // Call API - WebSocket will handle the real update
      await ProjectService.updateCollection(
        collectionToRename._id,
        userInfo.userId,
        { name: newName }
      );

      setRenamingCollection(null);
      console.log(`✅ Collection renamed successfully`);

    } catch (err) {
      console.error('❌ Error renaming collection:', err);
      // Revert optimistic update on error
      setCollections(prev =>
        prev.map(c =>
          c._id === collectionToRename._id ? collectionToRename : c
        )
      );
      setRenamingCollection(null);
      throw new Error(err.message || 'Failed to rename collection');
    }
  };

  const handleRenameCancel = () => {
    setRenamingCollection(null);
  };

  // File upload handlers - simplified to work like FilesView
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    try {
      // Validate we have project info
      if (!project || !project._id) {
        console.error('❌ Cannot upload: project is undefined or missing _id', project);
        setError('Project information is missing. Please refresh the page.');
        return;
      }

      if (!userInfo || !userInfo.userId) {
        console.error('❌ Cannot upload: userInfo is undefined or missing userId', userInfo);
        setError('User information is missing. Please refresh the page.');
        return;
      }

      // Determine project type from first file
      const firstFile = selectedFiles[0];
      const projectType = firstFile.type === 'application/pdf' ? 'pdf' : 'snippet';

      console.log('📤 Starting file upload to project root:', {
        projectId: project._id,
        projectName: project.name,
        fileCount: selectedFiles.length,
        projectType,
        userId: userInfo.userId
      });

      // Add files to processing queue with null collectionId and projectId for project root
      await FileProcessingService.addFilesToQueue(
        selectedFiles,
        projectType,
        null, // null collectionId = project root
        userInfo,
        project._id // Pass projectId for root uploads
      );

      // Since these are root files (no collectionId), we refresh the view to show them
      // We'll also notify parent about uploads to keep things consistent
      if (onCollectionUpdated) {
        // Use a special marker 'project-root' to indicate root file uploads
        selectedFiles.forEach(() => {
          onCollectionUpdated('project-root', 'fileUploaded');
        });
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh to show newly added root files
      setTimeout(() => fetchCollections(), 1000);

    } catch (error) {
      console.error('Error uploading files to project root:', error);
      setError(error.message || 'Failed to upload files');

      // Reset file input even on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileClick = (fileId, fileData) => {
    // Navigate to workspace with the specific file
    if (fileData?.uploadedFileId) {
      console.log('Opening file in workspace:', fileId, fileData);
      // TODO: Navigate to workspace with file
    }
  };

  const handleQueueChange = (status) => {
    // Update collections file count when files complete processing
    if (status.completed > 0) {
      // Refresh collections to show updated file counts
      fetchCollections();
    }
  };

  const handleDownloadProjectZip = async () => {
    if (!project || !userInfo?.userId) {
      console.error('Missing project or user data');
      return;
    }

    try {
      setIsDownloadingZip(true);
      
      // Fetch full collection details with files for all collections
      const collectionsWithFiles = await Promise.all(
        collections.map(async (collection) => {
          try {
            const fullCollection = await ProjectService.getCollectionDetails(
              collection._id,
              userInfo.userId
            );
            return fullCollection;
          } catch (error) {
            console.error(`Failed to fetch collection ${collection.name}:`, error);
            // Return collection without files if fetch fails
            return {
              ...collection,
              files: []
            };
          }
        })
      );
      
      const result = await exportService.downloadProjectCollectionsAsZip(
        project,
        collectionsWithFiles,
        rootFiles // Include root files if any
      );
      
      if (!result.success) {
        console.error('Failed to download ZIP:', result.error);
        alert('Failed to download project collections. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading project ZIP:', error);
      alert('An error occurred while downloading the project collections.');
    } finally {
      setIsDownloadingZip(false);
    }
  };


  // Expose handleNewCollection to parent via onNewCollection
  React.useEffect(() => {
    if (onNewCollection && typeof onNewCollection === 'function') {
      // Store the handler for external calls
      window.handleNewCollection = handleNewCollection;
    }
    return () => {
      delete window.handleNewCollection;
    };
  }, [onNewCollection]);

  // Merge cached collection data with fetched collections
  const mergedCollections = React.useMemo(() => {
    if (!collectionsCache || collectionsCache.size === 0) {
      return collections;
    }
    
    return collections.map(collection => {
      const cached = collectionsCache.get(collection._id);
      if (cached && cached.fileCount !== undefined) {
        // Use cached file count if available
        return {
          ...collection,
          fileCount: cached.fileCount
        };
      }
      return collection;
    });
  }, [collections, collectionsCache]);

  // Filter and sort collections (client-side search filtering)
  const sortedCollections = React.useMemo(() => {
    let filtered = mergedCollections;
    
    // Apply client-side search filtering
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = mergedCollections.filter(collection =>
        collection.name?.toLowerCase().includes(query) ||
        collection.description?.toLowerCase().includes(query) ||
        collection.tagDetails?.some(tag => tag.name?.toLowerCase().includes(query))
      );
    }
    
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'files':
          return (b.fileCount || 0) - (a.fileCount || 0);
        case 'modified':
        default:
          return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });
    
    return sorted;
  }, [mergedCollections, sortBy, searchQuery]);

  // Filter root files based on search query
  const filteredRootFiles = React.useMemo(() => {
    return rootFiles.filter(file => {
      // Skip placeholder files
      if (file.isRootFilesPlaceholder) return false;
      // If there's a search query, filter files that match
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          file.originalName?.toLowerCase().includes(query) ||
          file.name?.toLowerCase().includes(query) ||
          file.fileType?.toLowerCase().includes(query)
        );
      }
      // If no search query, show all root files
      return true;
    });
  }, [rootFiles, searchQuery]);

  // Pagination handlers
  const handleLoadMore = () => {
    if (pagination && pagination.currentPage < pagination.totalPages) {
      fetchCollections(currentPage + 1, searchQuery, false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchCollections(newPage, searchQuery, true);
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchCollections(newPage, searchQuery, true);
    }
  };

  // Handle root file clicks - open directly in workspace
  const handleRootFileClick = (file) => {
    // Get the file ID (could be _id or id depending on source)
    const fileId = file._id || file.id;

    // Security validation
    if (!file || !fileId || !project || !project._id || !onFileSelect) {
      console.warn('Invalid file or project data for navigation', { file, project });
      return;
    }

    // Additional validation for file data integrity
    if (typeof fileId !== 'string' && !fileId.toString) {
      console.error('Invalid file ID format');
      return;
    }

    try {
      // Transform file to match expected format for workspace with sanitization
      // Handle both initial load format and WebSocket update format
      onFileSelect({
        _id: fileId,
        id: fileId,  // Include both for compatibility
        originalName: file.originalName || file.name || 'Unnamed File',
        fileType: file.fileType || file.type || 'unknown',
        fileSize: Math.max(0, file.fileSize || 0),
        createdAt: file.createdAt,
        processingStatus: file.processingStatus || 'completed',
        tableCount: file.tableCount,
        projectId: project._id,
        projectName: project.name,
        isRootFile: true
      });
    } catch (error) {
      console.error('Error opening root file:', error);
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';

    const now = new Date();
    const past = new Date(date);
    const diffTime = Math.abs(now - past);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      return diffHours <= 1 ? 'Just now' : `${diffHours} hours ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  // Add visual indicator for processing status (same as FilesView)
  const getProcessingStatusBadge = (file) => {
    if (!file.processingStatus) return null;

    const statusConfig = {
      pending: { text: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
      processing: { text: 'Processing', className: 'bg-blue-500/20 text-blue-400 border-blue-500/50 animate-pulse' },
      completed: { text: 'Ready', className: 'bg-green-500/20 text-green-400 border-green-500/50' },
      failed: { text: 'Failed', className: 'bg-red-500/20 text-red-400 border-red-500/50' }
    };

    const config = statusConfig[file.processingStatus];
    if (!config) return null;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${config.className}`}>
        {config.text}
      </span>
    );
  };

  // Format file size helper
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  // Only show loading spinner if we have no data at all
  if (loading && collections.length === 0 && rootFiles.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">Error loading collections</div>
        <div className="text-slate-400 text-sm">{error}</div>
        <button
          onClick={() => fetchCollections()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-400 hover:text-slate-200 hover:bg-[#494646]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
          <div className="flex items-center gap-2 text-slate-500">
            <span>/</span>
            <span className="text-slate-300">{project?.name}</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        {!isCreatingCollection && (
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadProjectZip}
              disabled={isDownloadingZip || collections.length === 0}
              className="bg-[#494646] hover:bg-[#151415]/50 text-white disabled:opacity-50"
              size="sm"
              title={collections.length === 0 ? "No collections to download" : "Download all collections as ZIP"}
            >
              <Archive className="h-4 w-4 mr-2" />
              {isDownloadingZip ? 'Downloading...' : 'Export All'}
            </Button>
            <Button
              onClick={handleUploadClick}
              className="bg-[#494646] hover:bg-[#151415]/50 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Files
            </Button>
            <Button
              onClick={handleNewCollection}
              className="bg-[#494646] hover:bg-[#151415]/50 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Collection
            </Button>
          </div>
        )}
      </div>

      {/* Creation Error Display */}
      {createError && (
        <div className="p-4 bg-red-900/20 border border-red-700 rounded-lg text-red-400">
          {createError}
        </div>
      )}

      {/* Hidden file input for uploading files to project root */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Collections Display */}
      <div ref={scrollContainerRef} className="space-y-6">
        {viewMode === "list" ? (
          <div className="space-y-3">
            {/* Inline Collection Creator */}
            {isCreatingCollection && (
              <InlineCollectionCreator
                projectId={project?._id}
                userInfo={userInfo}
                viewMode="list"
                onCancel={handleCreateCancel}
                onSuccess={handleCreateSuccess}
                onError={handleCreateError}
              />
            )}
            
            {/* Show empty state if no collections, no root files, and not creating */}
            {sortedCollections.length === 0 && filteredRootFiles.length === 0 && !isCreatingCollection ? (
              <div className="text-center py-12">
                <FolderOpen className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <div className="text-slate-400 mb-2">
                  {searchQuery ? `No items found matching "${searchQuery}"` : "No collections or files found"}
                </div>
                <div className="text-slate-500 text-sm">
                  {searchQuery ? "Try adjusting your search terms." : "This project is empty."}
                </div>
              </div>
            ) : (
              <>
                {/* Collections */}
                {sortedCollections.map((collection) => (
                <Card
                  key={collection._id}
                  className="group drop-shadow-xl hover:shadow-lg cursor-pointer border-2 border-[#494646]/50 bg-[#2a282a] hover:border-[#494646]"
                  onClick={() => onCollectionClick && onCollectionClick(collection)}
                  onContextMenu={(e) => handleRightClick(e, collection)}
                >
              <CardContent className="px-5 py-5 mt-4">
                <div className="flex items-center justify-between gap-6">
                  {/* Left section - Icon and Content */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Icon */}
                    <div className="w-10 h-10  rounded-lg flex items-center justify-center flex-shrink-0">
                      <Layers3 className="h-5 w-5 text-blue-400" />
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Title */}
                      {renamingCollection && renamingCollection._id === collection._id ? (
                        <InlineRenameInput
                          initialValue={collection.name}
                          onConfirm={handleRenameConfirm}
                          onCancel={handleRenameCancel}
                          placeholder="Collection name..."
                          className="flex-1"
                        />
                      ) : (
                        <h3 className="font-semibold text-slate-100 text-lg leading-tight">
                          {collection.name}
                        </h3>
                      )}
                      
                      {/* Tags */}
                      {collection.tagDetails && collection.tagDetails.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap">
                          {collection.tagDetails.slice(0, 3).map(tag => (
                            <span
                              key={tag._id}
                              className="inline-flex items-center px-2.5 py-1 rounded text-xs bg-[#fef3ec] text-slate-800 font-bold border border-slate-600/50"
                            >
                              {tag.name}
                            </span>
                          ))}
                          {collection.tagDetails.length > 3 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded text-xs bg-[#fef3ec]/60 text-slate-800 font-bold border border-slate-600/30">
                              +{collection.tagDetails.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right section - Stats and Actions */}
                  <div className="flex items-center gap-6 text-sm text-slate-400 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <div className="text-center">
                        <div className="font-semibold text-slate-300">{collection.fileCount || 0}</div>
                        {/* <div className="text-xs text-slate-500">files</div> */}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <Users className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <div className="text-center">
                        <div className="font-semibold text-slate-300">{(collection.collaborators && collection.collaborators.length) || 0}</div>
                        {/* <div className="text-xs text-slate-500">users</div> */}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Clock className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <div className="text-center min-w-[80px]">
                        <div className="font-medium text-slate-300 text-xs">{formatTimeAgo(collection.updatedAt)}</div>
                        {/* <div className="text-xs text-slate-500">modified</div> */}
                      </div>
                    </div>
                  </div>
                </div>
                  </CardContent>
                </Card>
                ))}
                
                {/* Root Files */}
                {filteredRootFiles.map((file) => (
                  <Card
                    key={file._id || file.id}
                    className="group hover:shadow-lg cursor-pointer border-2 border-slate-700 bg-[#2a282a]/40 hover:border-green-500/50 hover:bg-[#151415]/30"
                    onClick={() => handleRootFileClick(file)}
                    onContextMenu={(e) => handleRightClickFile(e, file)}
                  >
                    <CardContent className="px-5 py-5 mt-4">
                      <div className="flex items-center justify-between gap-6">
                        {/* Left section - Icon and Content */}
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* Icon */}
                          <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 border border-slate-700/50">
                            <File className="h-5 w-5 text-green-400" />
                          </div>

                          {/* Main Content */}
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Title */}
                            <h3 className="font-semibold text-slate-100 text-base leading-tight">
                              {file.originalName || file.name}
                            </h3>

                            {/* File Type Badge */}
                            <span className="inline-flex items-center px-2.5 py-1 rounded text-xs bg-green-800/80 text-green-200 border border-green-600/50 font-medium">
                              {file.fileType?.toUpperCase() || file.type?.toUpperCase() || 'FILE'}
                            </span>

                            {/* Processing Status Badge */}
                            {getProcessingStatusBadge(file)}
                          </div>
                        </div>

                        {/* Right section - Stats */}
                        <div className="flex items-center gap-8 text-sm text-slate-400 flex-shrink-0">
                          {/* Table count if available */}
                          {file.tableCount !== undefined && file.tableCount !== null && (
                            <div className="flex items-center gap-2 min-w-0">
                              <Layers3 className="h-4 w-4 text-slate-500 flex-shrink-0" />
                              <div className="text-center">
                                <div className="font-semibold text-slate-300">{file.tableCount} tables</div>
                              </div>
                            </div>
                          )}

                          {/* File size */}
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                            <div className="text-center">
                              <div className="font-semibold text-slate-300">{formatFileSize(file.fileSize)}</div>
                            </div>
                          </div>

                          {/* Last modified */}
                          <div className="flex items-center gap-2 min-w-0">
                            <Clock className="h-4 w-4 text-slate-500 flex-shrink-0" />
                            <div className="text-center min-w-[80px]">
                              <div className="font-medium text-slate-300 text-xs">{formatTimeAgo(file.createdAt)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 px-6 py-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <span className="text-sm text-slate-500">
                    ({pagination.totalItems} total collections)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || loading}
                    className="border-slate-600 hover:border-blue-500 text-slate-300 hover:text-white"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= pagination.totalPages || loading}
                    className="border-slate-600 hover:border-blue-500 text-slate-300 hover:text-white"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Load More Button for infinite scroll style */}
            {pagination && pagination.currentPage < pagination.totalPages && !searchQuery && (
              <div className="text-center mt-6">
                <Button
                  variant="ghost"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-700 hover:border-blue-500"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                      Loading more...
                    </>
                  ) : (
                    `Load more collections (${pagination.totalItems - collections.length} remaining)`
                  )}
                </Button>
              </div>
            )}
            
            {/* Context Menu for List View */}
            <ContextMenu
              isOpen={contextMenu.isOpen}
              position={contextMenu.position}
              onClose={closeContextMenu}
              onRename={contextMenu.type === 'collection' ? handleRename : undefined}
              onDelete={handleDelete}
              onCollapse={contextMenu.type === 'collection' ? handleCollapse : undefined}
              itemName={contextMenu.type === 'collection' 
                ? contextMenu.collection?.name 
                : (contextMenu.file?.originalName || contextMenu.file?.name || '')}
              itemType={contextMenu.type || 'collection'}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {/* Inline Collection Creator */}
            {isCreatingCollection && (
              <InlineCollectionCreator
                projectId={project?._id}
                userInfo={userInfo}
                viewMode="grid"
                onCancel={handleCreateCancel}
                onSuccess={handleCreateSuccess}
                onError={handleCreateError}
              />
            )}
            
            {/* Show empty state if no collections, no root files, and not creating */}
            {sortedCollections.length === 0 && filteredRootFiles.length === 0 && !isCreatingCollection ? (
              <div className="col-span-full text-center py-12">
                <FolderOpen className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <div className="text-slate-400 mb-2">
                  {searchQuery ? `No items found matching "${searchQuery}"` : "No collections or files found"}
                </div>
                <div className="text-slate-500 text-sm">
                  {searchQuery ? "Try adjusting your search terms." : "This project is empty."}
                </div>
              </div>
            ) : (
              <>
                {/* Collections */}
                {sortedCollections.map((collection) => (
                <Card
                  key={collection._id}
                  className="group hover:shadow-xl drop-shadow-lg transition-all duration-200 cursor-pointer border-2 border-[#494646]/50 bg-[#2a282a] hover:border-[#fef3ec]/50 h-fit"
                  onClick={() => onCollectionClick && onCollectionClick(collection)}
                  onContextMenu={(e) => handleRightClick(e, collection)}
                >
              <CardContent className="p-2 space-y-3 mt-4">
                {/* Title with Icon */}
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  {renamingCollection && renamingCollection._id === collection._id ? (
                    <InlineRenameInput
                      initialValue={collection.name}
                      onConfirm={handleRenameConfirm}
                      onCancel={handleRenameCancel}
                      placeholder="Collection name..."
                      className="flex-1"
                      inputClassName="text-md"
                    />
                  ) : (
                    <h3 className="font-semibold text-lg text-slate-100 text-md line-clamp-1 leading-snug flex-1">
                      {collection.name}
                    </h3>
                  )}
                </div>

                {/* Tags Section - Fixed height to prevent layout shifts */}
                <div className="min-h-[20px] flex items-start">
                  {collection.tagDetails && collection.tagDetails.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {collection.tagDetails.slice(0, 2).map(tag => (
                        <span
                          key={tag._id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-[#fef3ec] text-slate-800 border border-slate-600/50 font-bold"
                        >
                          {/* {tag.name.length > 14 ? `${tag.name.slice(0, 14)}...` : tag.name} */}
                          {tag.name}
                        </span>
                      ))}
                      {collection.tagDetails.length > 2 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-800/60 text-slate-500 border border-slate-600/30">
                          +{collection.tagDetails.length - 2}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-600 italic">No tags</span>
                  )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-700/50">
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                    <span className="text-xs text-slate-400 font-medium">{collection.fileCount || 0}</span>
                    <span className="text-xs text-slate-600">files</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                    <span className="text-xs text-slate-400 font-medium">{(collection.collaborators && collection.collaborators.length) || 0}</span>
                    <span className="text-xs text-slate-600">users</span>
                  </div>
                </div>

                {/* Last Modified */}
                <div className="flex items-center gap-1.5 pt-1">
                  <Clock className="h-3 w-3 text-slate-600 flex-shrink-0" />
                  <span className="text-xs text-slate-500 truncate">{formatTimeAgo(collection.updatedAt)}</span>
                </div>
                  </CardContent>
                </Card>
                ))}
                
                {/* Root Files */}
                {filteredRootFiles.map((file) => (
                  <Card
                    key={file._id || file.id}
                    className="group hover:shadow-xl transition-all duration-200 cursor-pointer border-2 border-green-800 bg-[#2a282a]/40 hover:border-green-500/50 hover:bg-[#151415]/50 h-fit"
                    onClick={() => handleRootFileClick(file)}
                    onContextMenu={(e) => handleRightClickFile(e, file)}
                  >
                    <CardContent className="p-2 space-y-3 mt-4">
                      {/* Title with Icon */}
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-green-400 flex-shrink-0" />
                        <h3 className="font-semibold text-slate-100 text-md line-clamp-1 leading-snug flex-1">
                          {file.originalName || file.name}
                        </h3>
                      </div>

                      {/* File Info Section with Processing Status */}
                      <div className="min-h-[20px] flex items-start gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-900/80 text-green-200 border border-green-600/50 font-medium">
                          {file.fileType?.toUpperCase() || file.type?.toUpperCase() || 'FILE'}
                        </span>
                        {getProcessingStatusBadge(file)}
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-700/50">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                          <span className="text-xs text-slate-400 font-medium">{formatFileSize(file.fileSize).split(' ')[0]}</span>
                          <span className="text-xs text-slate-600">{formatFileSize(file.fileSize).split(' ')[1]}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {file.tableCount !== undefined && file.tableCount !== null ? (
                            <>
                              <Layers3 className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                              <span className="text-xs text-slate-400 font-medium">{file.tableCount}</span>
                              <span className="text-xs text-slate-600">tables</span>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 font-medium">{file.processingStatus || 'Ready'}</span>
                          )}
                        </div>
                      </div>

                      {/* Last Modified */}
                      <div className="flex items-center gap-1.5 pt-1">
                        <Clock className="h-3 w-3 text-slate-600 flex-shrink-0" />
                        <span className="text-xs text-slate-500 truncate">{formatTimeAgo(file.createdAt)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {/* Pagination Controls for Grid View */}
            {pagination && pagination.totalPages > 1 && (
              <div className="col-span-full flex items-center justify-between mt-8 px-6 py-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <span className="text-sm text-slate-500">
                    ({pagination.totalItems} total collections)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || loading}
                    className="border-slate-600 hover:border-blue-500 text-slate-300 hover:text-white"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= pagination.totalPages || loading}
                    className="border-slate-600 hover:border-blue-500 text-slate-300 hover:text-white"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Load More Button for Grid View */}
            {pagination && pagination.currentPage < pagination.totalPages && !searchQuery && (
              <div className="col-span-full text-center mt-6">
                <Button
                  variant="ghost"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-700 hover:border-blue-500"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                      Loading more...
                    </>
                  ) : (
                    `Load more collections (${pagination.totalItems - collections.length} remaining)`
                  )}
                </Button>
              </div>
            )}
            
            {/* Context Menu for Grid View */}
            <ContextMenu
              isOpen={contextMenu.isOpen}
              position={contextMenu.position}
              onClose={closeContextMenu}
              onRename={contextMenu.type === 'collection' ? handleRename : undefined}
              onDelete={handleDelete}
              onCollapse={contextMenu.type === 'collection' ? handleCollapse : undefined}
              itemName={contextMenu.type === 'collection' 
                ? contextMenu.collection?.name 
                : (contextMenu.file?.originalName || contextMenu.file?.name || '')}
              itemType={contextMenu.type || 'collection'}
            />
          </div>
        )}
      </div>
    </div>
  );
}